import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
//interfaces
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Grid, Typography, styled, Tooltip, useTheme } from "@mui/material";
import {
  IFlight,
  IFlightDelay,
  IFlightFlag,
  IFlightFlagSubDoc,
  IFlightPairingDetails,
  IFullPairingPosition,
  IGroundEvent,
  IGroundTimeInterval,
  sortCrewByDisplayOrder,
  StatusBarBehavior,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { formatDateString } from "src/components/DateDisplay";
import { DelayCodeChip } from "src/components/FilterSearchTable/CustomCellComponents/DelayCodeChip";
import { EventChip, GroupedEventChip } from "src/components/Flights/EventChip";
import { FlightChip } from "src/components/Flights/FlightChip";
import { IconButton } from "src/components/IconButton/IconButton";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { sortCrewMembersByPositionThenSeniority } from "src/pages/CrewSchedule/CrewScheduleWindow";
import { setSelectedCrewEvents } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { defaultTimezone, ICrewEvent, ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { IMosaicManager } from "src/redux/reducers/MosaicManagerReducer";
import { milli2DaysHrsMin } from "src/utils";
import { findMosaicNodeByComponentName } from "src/utils/MosaicTree";
import _ from "lodash";
import { renderFlagIcon } from "src/pages/FlightBuilder/Forms/CreateFlightFormik";

/**
 * Get the crew position and name
 * @param item - The Full Pairing Position
 */
const getCrewInfo = (item: IFlightPairingDetails): JSX.Element => {
  return (
    <Grid key={item?.position?.crew?._id} container className="crew_position_display">
      <PositionBadge
        position={item.position?.position}
        filled={!!item?.position?.crew}
        isInactive={!item?.position?.crew?.crewInfo?.isActive}
      />
      <Grid item style={{ marginLeft: 5 }}>
        {item?.position?.crew
          ? `${item?.position?.crew?.firstName ?? ""} ${item?.position?.crew?.lastName ?? ""}`
          : "Unassigned"}
      </Grid>
    </Grid>
  );
};

interface IPairingCrewMember extends ICrewMember {
  positionCode?: string;
}
/**
 * Get the crew info when pulling from a "Crew Schedule" event, which retrieves info in a different interface
 * from flight members"
 * @param item - Full pairing crewinfo
 */
const getPairingInfo = (item: IPairingCrewMember): JSX.Element => {
  const currentPosition = item.crewInfo.positions.find((position) => position.title == item.positionCode);
  return (
    <Grid key={item?._id} container className="crew_position_display">
      <PositionBadge position={currentPosition} filled={!!item} isInactive={!item?.crewInfo?.isActive} />
      <Grid item style={{ marginLeft: 5 }}>
        {item ? `${item?.firstName ?? ""} ${item?.lastName ?? ""}` : "Unassigned"}
      </Grid>
    </Grid>
  );
};

interface IPairingSummaryProps {
  selectedCrewEvent: any;
  crew?: IFullPairingPosition[];
}

const OCStatusBarSummary = styled("div")(({ theme }) => ({
  height: "100%",
  marginLeft: 3,
  zIndex: 1,
  display: "flex",
  alignItems: "inherit",
  whiteSpace: "nowrap",
  flex: "auto",
  overflow: "hidden",
  width: "80%",
  "& .border": {
    margin: "0px 10px",
    height: "75%",
    borderLeft: "1px solid white",
  },
  "& .summary_string": {
    marginLeft: "3px",
    height: "auto",
    maxWidth: "calc(100% - 100px)",
    display: "flex",
    alignItems: "center",
    textOverflow: "ellipsis",
    overflow: "hidden",
    fontSize: 12,
    fontWeight: 300,
    "& .deselect_event": {
      display: "none",
    },
    "&:hover": {
      "& .deselect_event": {
        display: "flex",
      },
    },
    "& .date_time": {
      padding: "3px",
      display: "flex",
      flexDirection: "column",
      "& div": { lineHeight: "14px" },
    },
    "& .arrow, & .deselect_event": {
      alignSelf: "center",
    },
    "& .origin, & .destination, & .location": {
      padding: "3px",
    },
    "& .crew_summary": {
      display: "flex",
      alignSelf: "center",
      "& .crew_position_display": {
        flexWrap: "nowrap",
        margin: "4px",
      },
    },
    "& .summary_flags": {
      display: "flex",
      alignSelf: "center",
      "& .flight_flag": {
        flexWrap: "nowrap",
        margin: "4px",
        paddingTop: "4px",
        "& svg": {
          height: "24px !important",
          width: "24px !important",
        },
      },
    },
    "& .small_note": {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      margin: "5px",
    },
  },
  "& .ground_time": {
    textDecoration: "underline",
  },
}));

/**
 * A function to take a pairing and return a div with the chip
 * & the a small summary of the pairing for the status bar
 */
export const PairingSummary = (props: IPairingSummaryProps): JSX.Element => {
  const { selectedCrewEvent } = props;
  const dispatch = useDispatch();
  const event = selectedCrewEvent?.event;
  const { _id, shortId, start, end, positions, assignmentType } = event;
  const crewMembers = useSelector((state: IRootState) => state.crewSchedule.crewMembers);

  const getCrewMember = useCallback(
    (crewId: string): IPairingCrewMember => {
      return crewId ? crewMembers?.find((c) => c?._id === crewId) : null;
    },
    [crewMembers]
  );

  const currentCrew = useMemo(() => {
    const crewArray: IPairingCrewMember[] = [];
    if (positions && positions.length > 0) {
      positions.map((position: any) => {
        const crewMember = getCrewMember(position.crew_id);
        if (crewMember) {
          crewMember.positionCode = position.position;
          crewArray.push(crewMember);
        }
      });
    }
    const sortedArray = crewArray.sort(sortCrewMembersByPositionThenSeniority);
    return sortedArray;
  }, [positions, getCrewMember]);

  const inUTC = useMemo(() => {
    return end && defaultTimezone?.timezoneId ? formatDateString(end, defaultTimezone.timezoneId) : null;
  }, [end]);

  const outUTC = useMemo(() => {
    return start && defaultTimezone?.timezoneId ? formatDateString(start, defaultTimezone.timezoneId) : null;
  }, [start]);

  const localTZ = useMemo(() => {
    return currentCrew && currentCrew[0]?.crewInfo?.bases[0]?.base?.timezoneId
      ? currentCrew[0].crewInfo.bases[0].base.timezoneId
      : "";
  }, [currentCrew]);

  const startLocal = useMemo(() => {
    return start && localTZ ? moment.utc(start).tz(localTZ)?.format("MMM Do, Y HH:mm z") : null;
  }, [start, localTZ]);

  const endLocal = useMemo(() => {
    return end && localTZ ? moment.utc(end).tz(localTZ)?.format("MMM Do, Y HH:mm z") : null;
  }, [end, localTZ]);

  const handleClick = (): void => {
    dispatch(setSelectedCrewEvents([], false, false, false));
  };

  //determine if the crew event is an NFA.
  //if so, clear the status bar
  if (assignmentType) {
    return <div></div>;
  }
  return (
    <OCStatusBarSummary>
      <ShortCodeChip
        code={shortId}
        itemId={_id}
        type="pairing"
        isPlanned={selectedCrewEvent?.isPlanned}
        className="status_bar"
      />
      <div className="summary_string">
        <div className="date_time">
          <div className="utc-time">{outUTC}</div>
          <div className="local-time">{startLocal}</div>
        </div>
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        <div className="date_time">
          <div className="utc-time">{inUTC}</div>
          <div className="local_time">{endLocal}</div>
        </div>
        {currentCrew?.length > 0 && (
          <div className="crew_summary">
            {currentCrew.map((crew: any, index: number) => {
              return <span key={`crew_summary_${index}`}>{getPairingInfo(crew)}</span>;
            })}
          </div>
        )}
        <div className="deselect_event">
          <IconButton
            id={"deselect_event_btn"}
            style={{ width: 36, height: 36, borderRadius: 5, margin: 4 }}
            onClick={handleClick}
            tooltipProps={{ title: "Deselect Event" }}
            size="large"
          >
            <CloseIcon className="deselect_event_icon" />
          </IconButton>
        </div>
      </div>
    </OCStatusBarSummary>
  );
};

interface IFlightSummaryProps {
  flight: IFlight;
  /** Only the details for pairings/crew where the position exists */
  crew?: IFlightPairingDetails[];
  eventSelected: boolean;
}
/**
 * A function to take a flight and return a div with the flight chip
 * & the a small summary of the flight for the status bar
 * @param flight - IFlight
 * @param dateFormat  - a string for moment .format()
 */
export const FlightSummary = (props: IFlightSummaryProps): JSX.Element => {
  const theme = useTheme();
  const { flight, crew, eventSelected } = props;
  const { actualOut, actualIn, destAirport, originAirport, departureGate, arrivalGate } = flight;
  const flightFlags = useSelector((state: IRootState) => state.structures.customerStructure.flightFlags);
  const dispatch = useDispatch();

  const outUTC = useMemo(() => {
    return actualOut && defaultTimezone?.timezoneId ? formatDateString(actualOut, defaultTimezone.timezoneId) : null;
  }, [actualOut]);

  const inUTC = useMemo(() => {
    return actualIn && defaultTimezone?.timezoneId ? formatDateString(actualIn, defaultTimezone.timezoneId) : null;
  }, [actualIn]);

  const startLocal = useMemo(() => {
    return actualOut && originAirport?.timezoneId
      ? moment.utc(actualOut).tz(originAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [actualOut, originAirport]);

  const endLocal = useMemo(() => {
    return actualIn && destAirport?.timezoneId
      ? moment.utc(actualIn).tz(destAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [actualIn, destAirport]);

  const flightDelays: IFlightDelay[] = useMemo(() => (flight as any)?.delays ?? [], [flight]);
  const summaryInfo = outUTC && inUTC && startLocal && endLocal;

  // Sort crew by displayOrder and if a match, by masterSeniority
  const sortedCrew = useMemo(() => {
    if (!crew || crew?.length < 1) {
      return [];
    } else {
      return crew.sort((a, b) => (a.position && b.position ? sortCrewByDisplayOrder(a.position, b.position) : -1));
    }
  }, [crew]);

  if (!flight) {
    return null;
  }

  const handleClick = (): void => {
    dispatch(setSelectedEvents([]));
  };

  return (
    <OCStatusBarSummary>
      <FlightChip data={flight} className="status_bar" />
      {summaryInfo && (
        <div className="summary_string">
          <div className="date_time">
            <div className="utc_time">{outUTC}</div>
            <div className="local_time">{startLocal}</div>
          </div>
          <div className="arrow">
            <ArrowForwardIcon className="arrow_forward_icon" />
          </div>
          <div className="date_time">
            <div className="utc_time">{inUTC}</div>
            <div className="local_time">{endLocal}</div>
          </div>
          <Box
            className="gate_summary"
            sx={{
              marginLeft: "8px",
              padding: "2px 8px",
              borderRadius: theme.shape.borderRadius * 2,
              backgroundColor: theme.custom.statusBar.backgroundColor,
              color: theme.custom.statusBar.foregroundColor,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            <span>Dep gate: {departureGate || <span style={{ opacity: 0.7 }}>n/a</span>}</span>
            <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
            <span>Arr gate: {arrivalGate || <span style={{ opacity: 0.7 }}>n/a</span>}</span>
          </Box>
          {sortedCrew?.length > 0 && (
            <div className="crew_summary">
              {sortedCrew.map((crew: IFlightPairingDetails, index) => {
                return <span key={`crew_summary_${index}`}>{getCrewInfo(crew)}</span>;
              })}
            </div>
          )}
          {flight.customer?.length > 0 && (
            <div className="customer_summary">
              {flight.customer.map((cust, idx) => {
                return (
                  <Typography
                    key={`cust_${idx}`}
                    variant="body2"
                    sx={{
                      marginLeft: "8px",
                      padding: "2px 8px",
                      borderRadius: theme.shape.borderRadius * 2,
                      backgroundColor: theme.custom.statusBar.backgroundColor,
                      color: theme.custom.statusBar.foregroundColor,
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      boxShadow: theme.shadows[2],
                      border: `1px solid ${theme.palette.primary.main}`,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    {cust.name}
                  </Typography>
                );
              })}
            </div>
          )}

          {flight.flags.length > 0 && (
            <div className="summary_flags">
              {flight.flags.map((flag: IFlightFlagSubDoc, idx: number) => {
                const fullFlightFlag = flightFlags.find((structureFlag: IFlightFlag) => {
                  return flag._id === structureFlag._id;
                });
                return (
                  <div key={idx} className="flight_flag">
                    <Tooltip title={`${fullFlightFlag.title} (${fullFlightFlag.description})`}>
                      {renderFlagIcon(fullFlightFlag.icon)}
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}

          {flightDelays?.length ? (
            <Grid container alignItems="flex-end" sx={{ marginBottom: "2px" }}>
              <Typography sx={{ margin: "0px 10px" }} variant="caption">
                Delays:
              </Typography>
              {flightDelays
                ?.sort((a, b) => {
                  if (a.onDeparture !== b.onDeparture) {
                    return a.onDeparture ? -1 : 1;
                  }
                  return a.duration - b.duration;
                })
                .map((delay) => (
                  <DelayCodeChip
                    delay={delay.delay}
                    showFullName={false}
                    flightDelay={delay}
                    aircraft={flight?.aircraft}
                    showDuration={true}
                    key={delay._id}
                  />
                ))}
            </Grid>
          ) : (
            <></>
          )}

          <div className="deselect_event">
            {eventSelected && (
              <IconButton
                id={"deselect_event_btn"}
                style={{ width: 36, height: 36, borderRadius: 5, margin: 4 }}
                onClick={handleClick}
                tooltipProps={{ title: "Deselect Event" }}
                size="large"
              >
                <CloseIcon className="deselect_event_icon" />
              </IconButton>
            )}
          </div>
        </div>
      )}
    </OCStatusBarSummary>
  );
};

/**
 * A function to take a ground event and return a div with the ground event chip
 * & the a small summary of the GE for the status bar
 * @param groundEvent - IGroundEvent
 * @param dateFormat  - a string for moment .format()
 */

interface IGroundEventSummaryProps {
  groundEvent: IGroundEvent;
  smallNote?: string;
  eventSelected?: boolean;
  crew?: IFlightPairingDetails[];
}

const GroundEventSummary = (props: IGroundEventSummaryProps): JSX.Element => {
  const { groundEvent, smallNote, eventSelected, crew } = props;
  const { actualStart, actualEnd, locationAirport } = groundEvent;

  const startUTC = useMemo(() => {
    return actualStart && defaultTimezone?.timezoneId
      ? formatDateString(actualStart, defaultTimezone.timezoneId)
      : null;
  }, [actualStart]);

  const endUTC = useMemo(() => {
    return actualEnd && defaultTimezone?.timezoneId ? formatDateString(actualEnd, defaultTimezone.timezoneId) : null;
  }, [actualEnd]);

  const startLocal = useMemo(() => {
    return actualStart && locationAirport?.timezoneId
      ? moment.utc(actualStart).tz(locationAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [actualStart, locationAirport]);

  const endLocal = useMemo(() => {
    return actualEnd && locationAirport?.timezoneId
      ? moment.utc(actualEnd).tz(locationAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [actualEnd, locationAirport]);

  const sortedCrew = useMemo(() => {
    if (!crew || crew?.length < 1) {
      return [];
    } else {
      return crew.sort((a, b) => (a.position && b.position ? sortCrewByDisplayOrder(a.position, b.position) : -1));
    }
  }, [crew]);

  const summaryInfo = startUTC && endUTC && startLocal && endLocal;

  if (!groundEvent) {
    return null;
  }
  return (
    <OCStatusBarSummary>
      <EventChip data={groundEvent} className="status_bar" />
      {summaryInfo && (
        <div className="summary_string">
          <div className="date_time">
            <div className="utc_time">{startUTC}</div>
            <div className="local_time">{startLocal}</div>
          </div>
          <div className="arrow">
            <ArrowForwardIcon className="arrow_forward_icon" />
          </div>
          <div className="date_time">
            <div className="utc_time">{endUTC}</div>
            <div className="local_time">{endLocal}</div>
          </div>
          {sortedCrew?.length > 0 && (
            <div className="crew_summary">
              {sortedCrew.map((crew: IFlightPairingDetails, index) => {
                return <span key={`crew_summary_${index}`}>{getCrewInfo(crew)}</span>;
              })}
            </div>
          )}
          {eventSelected && <div className="small_note">{smallNote}</div>}
        </div>
      )}
    </OCStatusBarSummary>
  );
};

/**
 * A function to take a flight and return a div with the flight chip
 * & the a small summary of the flight for the status bar
 * @param flight - IFlight
 * @param dateFormat  - a string for moment .format()
 */
export const GroundTimeSummary = (): JSX.Element => {
  const hoveredGroundTimeInterval: IGroundTimeInterval = useSelector(
    (state: IRootState) => state.timelineFlights.hoveredGroundTimeInterval
  );

  const prevEvent = useMemo(() => {
    return hoveredGroundTimeInterval?.prevEvent;
  }, [hoveredGroundTimeInterval]);

  const nextEvent = useMemo(() => {
    return hoveredGroundTimeInterval?.nextEvent;
  }, [hoveredGroundTimeInterval]);

  const prevFlight = useMemo(() => {
    return hoveredGroundTimeInterval?.prevFlight;
  }, [hoveredGroundTimeInterval]);

  const nextFlight = useMemo(() => {
    return hoveredGroundTimeInterval?.nextFlight;
  }, [hoveredGroundTimeInterval]);

  const groundEvents = useMemo(() => {
    return hoveredGroundTimeInterval?.groundEvents;
  }, [hoveredGroundTimeInterval]);

  //if flight actualIn, if GE actual end
  const prevEventEndLocal = useMemo(() => {
    return isFlight(prevEvent)
      ? prevEvent?.actualIn && prevEvent?.destAirport?.timezoneId
        ? moment.utc(prevEvent.actualIn).tz(prevEvent?.destAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
        : null
      : prevEvent?.actualEnd && prevEvent?.locationAirport?.timezoneId
        ? formatDateString(prevEvent.actualEnd, prevEvent?.locationAirport?.timezoneId)
        : null;
  }, [prevEvent]);

  const prevEventEndUTC = useMemo(() => {
    return isFlight(prevEvent)
      ? prevEvent?.actualIn && defaultTimezone?.timezoneId
        ? formatDateString(prevEvent.actualIn, defaultTimezone.timezoneId)
        : null
      : prevEvent?.actualStart && defaultTimezone?.timezoneId
        ? formatDateString(prevEvent.actualStart, defaultTimezone.timezoneId)
        : null;
  }, [prevEvent]);

  const prevFlightEndLocal = useMemo(() => {
    return prevFlight?.actualIn && prevFlight?.destAirport?.timezoneId
      ? moment.utc(prevFlight.actualIn).tz(prevFlight?.destAirport?.timezoneId)?.format("MMM Do, Y HH:mm Z")
      : null;
  }, [prevFlight]);

  const prevFlightEndUTC = useMemo(() => {
    return prevFlight?.actualIn && defaultTimezone?.timezoneId
      ? formatDateString(prevFlight.actualIn, defaultTimezone.timezoneId)
      : null;
  }, [prevFlight]);

  //if flight actual out, if GE actual start
  const nextEventStartLocal = useMemo(() => {
    return isFlight(nextEvent)
      ? nextEvent?.actualOut && nextEvent?.originAirport?.timezoneId
        ? moment.utc(nextEvent.actualOut).tz(nextEvent?.originAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
        : null
      : nextEvent?.actualStart && nextEvent?.locationAirport?.timezoneId
        ? formatDateString(nextEvent.actualStart, nextEvent?.locationAirport?.timezoneId)
        : null;
  }, [nextEvent]);

  const nextEventStartUTC = useMemo(() => {
    return isFlight(nextEvent)
      ? nextEvent?.actualOut && defaultTimezone?.timezoneId
        ? formatDateString(nextEvent.actualOut, defaultTimezone.timezoneId)
        : null
      : nextEvent?.actualEnd && defaultTimezone?.timezoneId
        ? formatDateString(nextEvent.actualEnd, defaultTimezone.timezoneId)
        : null;
  }, [nextEvent]);

  const nextFlightStartLocal = useMemo(() => {
    return nextFlight?.actualOut && nextFlight?.originAirport?.timezoneId
      ? moment.utc(nextFlight.actualOut).tz(nextFlight?.originAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [nextFlight]);

  const nextFlightStartUTC = useMemo(() => {
    return nextFlight?.actualOut && defaultTimezone?.timezoneId
      ? formatDateString(nextFlight.actualOut, defaultTimezone.timezoneId)
      : null;
  }, [nextFlight]);

  const groundTime = useMemo(() => {
    const dif =
      (isFlight(nextEvent) ? nextEvent?.actualOut?.valueOf() : nextEvent?.actualStart?.valueOf()) -
      (isFlight(prevEvent) ? prevEvent?.actualIn?.valueOf() : prevEvent?.actualEnd?.valueOf());
    return dif ? milli2DaysHrsMin(dif) : null;
  }, [prevEvent, nextEvent]);

  const flightToGroundTime = useMemo(() => {
    if (!prevFlight || groundEvents?.length == 0) return null;
    const dif = groundEvents[0].actualStart.valueOf() - prevFlight.actualIn.valueOf();
    return dif ? milli2DaysHrsMin(dif) : null;
  }, [groundEvents, prevFlight]);

  const groundToFlightTime = useMemo(() => {
    if (!nextFlight || groundEvents?.length == 0) return null;
    const dif = nextFlight.actualOut.valueOf() - groundEvents[groundEvents.length - 1].actualEnd.valueOf();
    return dif ? milli2DaysHrsMin(dif) : null;
  }, [groundEvents, nextFlight]);

  const flightTotalTime = useMemo(() => {
    const dif = nextFlight?.actualOut?.valueOf() - prevFlight?.actualIn?.valueOf();
    return dif ? milli2DaysHrsMin(dif) : null;
  }, [prevFlight, nextFlight]);

  if (!prevFlight || !nextFlight || (_.isEqual(prevEvent, prevFlight) && _.isEqual(nextEvent, nextFlight))) {
    return (
      <OCStatusBarSummary>
        {isFlight(prevEvent) ? (
          <FlightChip data={prevEvent} className="status_bar" />
        ) : (
          <EventChip data={prevEvent} className="status_bar" />
        )}
        <div className="summary_string">
          <div className="date_time">
            <div className="utc_time">{prevEventEndUTC}</div>
            <div className="local_time">{prevEventEndLocal}</div>
          </div>
        </div>
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        <div className="ground_time">{groundTime}</div>
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        {isFlight(nextEvent) ? (
          <FlightChip data={nextEvent} className="status_bar" />
        ) : (
          <EventChip data={nextEvent} className="status_bar" />
        )}
        <div className="summary_string">
          <div className="date_time">
            <div className="utc_time">{nextEventStartUTC}</div>
            <div className="local_time">{nextEventStartLocal}</div>
          </div>
        </div>
      </OCStatusBarSummary>
    );
  } else {
    return (
      <OCStatusBarSummary>
        <FlightChip data={prevFlight} className="status_bar" />
        <div className="summary_string">
          <div className="date_time">
            <div className="utc_time">{prevFlightEndUTC}</div>
            <div className="local_time">{prevFlightEndLocal}</div>
          </div>
        </div>
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        <div className="ground_time">{flightToGroundTime}</div>
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        {groundEvents.length > 1 && <GroupedEventChip data={groundEvents} />}
        {groundEvents.length === 1 && <EventChip data={groundEvents[0]} className="status_bar" showDuration={true} />}
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        <div className="ground_time">{groundToFlightTime}</div>
        <div className="arrow">
          <ArrowForwardIcon className="arrow_forward_icon" />
        </div>
        <FlightChip data={nextFlight} className="status_bar" />
        <div className="summary_string">
          <div className="date_time">
            <div className="utc_time">{nextFlightStartUTC}</div>
            <div className="local_time">{nextFlightStartLocal}</div>
          </div>
        </div>
        <div className="border" />
        <div className="ground_time">{flightTotalTime}</div>
      </OCStatusBarSummary>
    );
  }
};

/**
 * A componet to display a summary of a hovered events in the app status bar
 * It gets the event from redux - currently only displaying the hovered events
 * in the flight timeline
 */
export const StatusBarSummary = (): JSX.Element => {
  const hoveredFlightEvent: any = useSelector((state: IRootState) => state.timelineFlights.hoveredFlight);
  const hoveredGroundTimeInterval: IGroundTimeInterval = useSelector(
    (state: IRootState) => state.timelineFlights.hoveredGroundTimeInterval
  );

  const hoveredCrewSchedule: any = useSelector((state: IRootState) => state.crewSchedule.hoveredEvents.hoveredEvents);
  const statusBehavior = useSelector((state: IRootState) => state.authentication.appSettings.statusBarBehavior);
  const selectedEvents: (IGroundEvent | IFlight)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  const selectedCrewEvents: ICrewEvent[] = useSelector((state: IRootState) => state.crewSchedule.selectedCrewEvents);

  const windowManager: IMosaicManager = useSelector((state: IRootState) => state.windowManager);

  const showGroundTime = true; //make this into a user setting

  const [currentEvent, setCurrentEvent] = useState(null);
  const dispatch = useDispatch();

  const isAFlightDisplayOpen = useMemo(() => {
    return findMosaicNodeByComponentName(windowManager.currentNode, "FlightTimeline");
  }, [windowManager]);

  const selectedEvent = useMemo(() => {
    setCurrentEvent("flight");
    return selectedEvents.length === 1 ? selectedEvents?.[0] : null;
  }, [selectedEvents]);

  const selectedCrewEvent = useMemo(() => {
    setCurrentEvent("crew");
    return selectedCrewEvents.length === 1 ? selectedCrewEvents[0] : null;
  }, [selectedCrewEvents]);

  useEffect(() => {
    if (!isAFlightDisplayOpen && !!selectedEvent) {
      //since there are no flight display windows open, dispatch to deselect the event...
      dispatch(setSelectedEvents([]));
    }
  }, [dispatch, isAFlightDisplayOpen, selectedEvent]);

  const [crew, setCrew] = useState<IFlightPairingDetails[]>([]);
  const [smallNote, setSmallNote] = useState<string>(undefined); //DB has a limit of 100 chars for this field

  //get crew on flight...
  const getCrewOnFlight = useCallback(async (): Promise<void> => {
    setCrew([]);
    if (!selectedEvent?._id || selectedEvent?._id.includes("temp")) {
      //if there is no id or it is a temp flight or it is a ground event, do not try to fetch the crew
      return;
    }

    const response = isFlight(selectedEvent)
      ? await AmplifyAPIWrapper.Get<Record<string, IFlightPairingDetails[]>>(`/flight/${selectedEvent._id}/crew`)
      : await AmplifyAPIWrapper.Get<Record<string, IFlightPairingDetails[]>>(`/groundEvents/${selectedEvent._id}/crew`);
    if (response?.crew || response?.positioningCrew) {
      setCrew(response.crew);
    }
  }, [selectedEvent]);

  //get small note on ground event
  const getSmallNoteOnGroundEvent = useCallback(async (): Promise<void> => {
    setSmallNote(undefined);
    if (!selectedEvent?._id || selectedEvent?._id.includes("temp") || isFlight(selectedEvent)) {
      //if there is no id or it is a temp ground event or not a ground event don't try to fetch the note
      return;
    }
    const response = await AmplifyAPIWrapper.Get<IGroundEvent>(`/groundEvents/${selectedEvent._id}`);
    if (response) {
      setSmallNote(response.note);
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedEvent) {
      getCrewOnFlight();
      getSmallNoteOnGroundEvent();
    }
  }, [getCrewOnFlight, selectedEvent, getSmallNoteOnGroundEvent]);

  const summaryDisplayElement: JSX.Element = useMemo(() => {
    // Only display selected flight summary if hovered ground time is not set.
    if (statusBehavior == StatusBarBehavior.Sticky) {
      if (currentEvent == "flight") {
        if (selectedEvent && !hoveredGroundTimeInterval?.nextEvent) {
          const typeFlight = isFlight(selectedEvent) ? true : false;
          if (typeFlight) {
            return (
              <FlightSummary
                flight={selectedEvent as IFlight}
                crew={selectedEvent?._id ? crew : null}
                eventSelected={true}
              />
            );
          } else {
            return (
              <GroundEventSummary
                groundEvent={selectedEvent as IGroundEvent}
                smallNote={smallNote}
                eventSelected={true}
                crew={crew ?? null}
              />
            );
          }
        } else if (showGroundTime && !!hoveredGroundTimeInterval?.prevEvent && !!hoveredGroundTimeInterval?.nextEvent) {
          return <GroundTimeSummary />;
        } else {
          return <div></div>;
        }
      } else if (currentEvent == "crew" && !!selectedCrewEvent) {
        return <PairingSummary selectedCrewEvent={selectedCrewEvent} />;
      }
    } else if (statusBehavior == StatusBarBehavior.Hover) {
      if (hoveredFlightEvent) {
        const typeFlight = isFlight(hoveredFlightEvent) ? true : false;
        if (typeFlight) {
          return (
            <FlightSummary
              flight={hoveredFlightEvent as IFlight}
              crew={hoveredFlightEvent?._id ? crew : null}
              eventSelected={true}
            />
          );
        } else {
          return (
            <GroundEventSummary
              groundEvent={hoveredFlightEvent as IGroundEvent}
              smallNote={smallNote}
              crew={crew ?? null}
              eventSelected={true}
            />
          );
        }
      } else if (hoveredCrewSchedule?.[0]) {
        return <PairingSummary selectedCrewEvent={hoveredCrewSchedule[0]} />;
      }
    }
    return <div></div>;
  }, [
    crew,
    hoveredCrewSchedule,
    selectedEvent,
    selectedCrewEvent,
    smallNote,
    showGroundTime,
    currentEvent,
    hoveredGroundTimeInterval,
    statusBehavior,
    hoveredFlightEvent,
  ]);

  return summaryDisplayElement;
};
